'use client';
import React from 'react';
import Accordion from '@/components/ui/Accordion';
import { faqItems } from '@/data/productData';
import { FAQItem } from '@/types/pdp';
import { useFAQ } from '@/hooks/useFAQ';
import { RichTextNode } from '@/types/faq';

interface FAQSectionMobileProps {
  faqItems?: FAQItem[];
  useStrapiFAQ?: boolean;
  productName?: string;
}

const FAQSectionMobile: React.FC<FAQSectionMobileProps> = ({
  faqItems: customFaqItems,
  useStrapiFAQ = false,
  productName,
}) => {
  const { faqData, loading } = useFAQ();

  // Función para renderizar texto enriquecido de Strapi
  const renderRichText = (richTextNodes: RichTextNode[] | undefined): React.ReactNode => {
    if (!richTextNodes || richTextNodes.length === 0) return null;
    
    return richTextNodes.map((paragraph: any, pIndex: number) => {
      if (!paragraph.children) return null;
      
      return (
        <React.Fragment key={pIndex}>
          {paragraph.children.map((child: any, cIndex: number) => {
            if (child.bold) {
              return (
                <span data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:34:16" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="34" data-component-file="FAQSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-bold%22%7D" 
                  key={cIndex} 
                  className="font-tt-norms-bold"
                  style={{ fontFamily: "'TT_Norms_Pro-Bold', 'TT Norms Pro', sans-serif" }}
                >
                  {child.text}
                </span>
              );
            }
            return <React.Fragment key={cIndex}>{child.text}</React.Fragment>;
          })}
        </React.Fragment>
      );
    });
  };

  // Convertir preguntas de rich text a string simple para el título
  const richTextToString = (richTextNodes: RichTextNode[] | undefined): string => {
    if (!richTextNodes || richTextNodes.length === 0) return '';
    
    return richTextNodes
      .map(paragraph => 
        paragraph.children
          .map(child => child.text)
          .join('')
      )
      .join(' ');
  };

  // Convertir datos mock al formato de Strapi si no hay datos personalizados
  const mockFaqItems: FAQItem[] = faqItems.map((item, index) => ({
    id: index + 1,
    Pregunta: item.title,
    Respuesta: item.content
  }));

  // Determinar qué items mostrar
  let accordionItems;
  
  if (useStrapiFAQ && faqData?.data?.FAQ_Preguntas_Frecuentes) {
    // Usar datos de Strapi
    accordionItems = faqData.data.FAQ_Preguntas_Frecuentes.map((faq) => ({
      title: richTextToString(faq.Pregunta),
      content: renderRichText(faq.Respuesta)
    }));
  } else if (customFaqItems && customFaqItems.length > 0) {
    // Usar datos personalizados
    accordionItems = customFaqItems.map(item => ({
      title: item.Pregunta,
      content: item.Respuesta
    }));
  } else {
    // Usar datos mock
    accordionItems = mockFaqItems.map(item => ({
      title: item.Pregunta,
      content: item.Respuesta
    }));
  }

  // Estado de carga solo si estamos usando Strapi
  if (useStrapiFAQ && loading) {
    return (
      <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:96:6" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="96" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%22%7D" className="p-4">
        <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:97:8" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="97" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:98:10" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="98" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-gray-300%20rounded%20mb-4%20w-64%22%7D" className="h-8 bg-gray-300 rounded mb-4 w-64"></div>
          <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:99:10" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="99" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gray-300%20rounded-none%20w-48%20h-6%20mb-8%22%7D" className="bg-gray-300 rounded-none w-48 h-6 mb-8"></div>
          <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:100:10" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="100" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:101:12" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="101" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-gray-300%20rounded%22%7D" className="h-12 bg-gray-300 rounded"></div>
            <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:102:12" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="102" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-gray-300%20rounded%22%7D" className="h-12 bg-gray-300 rounded"></div>
            <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:103:12" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="103" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-gray-300%20rounded%22%7D" className="h-12 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:111:4" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="111" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%22%7D" className="p-4">
      <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:112:6" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="112" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20mb-3%20text-left%22%7D" className="px-4 mb-3 text-left">
        {productName ? (
          <>
            <h2 data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:115:12" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="115" data-component-file="FAQSectionMobile.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22sr-only%22%2C%22textContent%22%3A%22Preguntas%20frecuentes%20sobre%22%7D" className="sr-only">Preguntas frecuentes sobre {productName}</h2>
            <p data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:116:12" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="116" data-component-file="FAQSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-2xl%20leading-7%20text-global-1%20mb-1%22%2C%22textContent%22%3A%22PREGUNTAS%20FRECUENTES%22%7D" className="font-tt-norms-extra-black text-2xl leading-7 text-global-1 mb-1">
              PREGUNTAS FRECUENTES
            </p>
          </>
        ) : (
          <h2 data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:121:10" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="121" data-component-file="FAQSectionMobile.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-2xl%20leading-7%20text-global-1%20mb-1%22%2C%22textContent%22%3A%22PREGUNTAS%20FRECUENTES%22%7D" className="font-tt-norms-extra-black text-2xl leading-7 text-global-1 mb-1">
            PREGUNTAS FRECUENTES
          </h2>
        )}
        <div data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:125:8" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="125" data-component-file="FAQSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded-none%20px-3%20py-1%20w-fit%20mb-4%22%7D" className="bg-global-9 rounded-none px-3 py-1 w-fit mb-4">
          <span data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:126:10" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="126" data-component-file="FAQSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20font-tt-norms%20font-medium%20text-sm%20leading-4%22%2C%22textContent%22%3A%22Seguro%20que%20te%20interesa%22%7D" className="text-global-1 font-tt-norms font-medium text-sm leading-4">
            Seguro que te interesa
          </span>
        </div>
      </div>
      <Accordion data-component-id="src/components/sections/mobile/FAQSectionMobile.tsx:131:6" data-component-path="src/components/sections/mobile/FAQSectionMobile.tsx" data-component-line="131" data-component-file="FAQSectionMobile.tsx" data-component-name="Accordion" data-component-content="%7B%22elementName%22%3A%22Accordion%22%7D" items={accordionItems} />
    </div>
  );
};

export default FAQSectionMobile; 